import { useEffect, useRef } from 'react'

import { GO_TO_SHORTCUTS } from '@/components/layout/nav-config'

/**
 * The app-wide shortcuts from docs/00-roadmap.md Part 3.
 *
 *   /          focus search
 *   Ctrl/⌘+K   command palette
 *   ?          shortcut cheatsheet
 *   g then …   jump to a section (d, e, i, b, c, r)
 *
 * Everything except ⌘K is suppressed while focus is in a text field: someone
 * typing "g" into a narration box must not be teleported to the dashboard.
 * ⌘K survives because no text field means anything by it.
 */

export interface GlobalShortcutHandlers {
  onFocusSearch: () => void
  onOpenCommandPalette: () => void
  onOpenShortcuts: () => void
  onGoTo: (path: string) => void
}

/** How long a pending `g` waits for its second key. */
const SEQUENCE_TIMEOUT_MS = 1000

function isTextEntry(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) return false
  if (target.isContentEditable) return true

  const tag = target.tagName
  if (tag === 'TEXTAREA' || tag === 'SELECT') return true
  if (tag === 'INPUT') {
    // Checkboxes, radios and buttons are inputs but hold no text, so a
    // shortcut pressed on one is unambiguous.
    const type = (target as HTMLInputElement).type
    return !['checkbox', 'radio', 'button', 'submit', 'reset', 'range', 'file'].includes(type)
  }
  return false
}

export function useGlobalShortcuts(handlers: GlobalShortcutHandlers): void {
  // Held in a ref so the listener is attached once and never re-bound when a
  // parent re-renders with fresh closures.
  const handlersRef = useRef(handlers)
  handlersRef.current = handlers

  useEffect(() => {
    let pendingSequence = false
    let timer: ReturnType<typeof setTimeout> | undefined

    const clearSequence = () => {
      pendingSequence = false
      if (timer !== undefined) clearTimeout(timer)
      timer = undefined
    }

    const onKeyDown = (event: KeyboardEvent) => {
      // A dialog or combobox that already acted on this key owns it.
      if (event.defaultPrevented) return

      if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === 'k') {
        event.preventDefault()
        clearSequence()
        handlersRef.current.onOpenCommandPalette()
        return
      }

      // Modified keys beyond ⌘K belong to the browser or the OS.
      if (event.metaKey || event.ctrlKey || event.altKey) return
      if (isTextEntry(event.target)) return

      if (pendingSequence) {
        const target = GO_TO_SHORTCUTS.find((shortcut) => shortcut.key === event.key.toLowerCase())
        clearSequence()
        if (target) {
          event.preventDefault()
          handlersRef.current.onGoTo(target.path)
        }
        return
      }

      switch (event.key) {
        case '/':
          event.preventDefault()
          handlersRef.current.onFocusSearch()
          return

        case '?':
          event.preventDefault()
          handlersRef.current.onOpenShortcuts()
          return

        case 'g':
        case 'G':
          // Not prevented: `g` alone means nothing, and swallowing it would
          // break find-as-you-type in the browser.
          pendingSequence = true
          timer = setTimeout(clearSequence, SEQUENCE_TIMEOUT_MS)
          return

        default:
          return
      }
    }

    document.addEventListener('keydown', onKeyDown)
    return () => {
      document.removeEventListener('keydown', onKeyDown)
      clearSequence()
    }
  }, [])
}
